import { createParser } from 'eventsource-parser';
import { apiUrl } from '../config/env';

/** 与后端 ChatStreamEventDto 一一对应。类型写在这里是因为 SSE 不是 orval 能生成的形态。 */
export type ChatStreamEvent =
  | { event: 'text'; delta: string; sessionId: string }
  | { event: 'done'; sessionId: string }
  | { event: 'error'; sessionId: string; errorMessage: string };

export interface StreamChatOptions {
  prompt: string;
  sessionId?: string;
  signal?: AbortSignal;
  onEvent: (event: ChatStreamEvent) => void;
}

/**
 * 消费后端的 SSE 流。
 *
 * 为什么不用原生 EventSource：
 *   EventSource 只支持 GET，且不能自定义请求头、不能带请求体。
 *   对话场景需要 POST 传长文本，所以只能自己读 ReadableStream。
 *
 * 为什么用 eventsource-parser 而不是手写帧解析：
 *   手写版（2026-09 原型）曾要求自己处理"一帧被拆到两次 read() 之间"的缓冲——
 *   这是 SSE 流消费唯一真正微妙的地方。eventsource-parser 是 OpenAI 官方 SDK
 *   同款解析器（活跃维护），把这段脆弱逻辑外包给它，我们只保留传输与 JSON 层。
 *   原 4 个行为单测全部保留，作为切换后的行为回归。
 */
export async function streamChat({
  prompt,
  sessionId,
  signal,
  onEvent,
}: StreamChatOptions): Promise<void> {
  const response = await fetch(apiUrl('/api/v1/chat/stream'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
    body: JSON.stringify({ prompt, sessionId }),
    signal,
  });

  if (!response.ok) {
    // 流没开始就失败时，后端仍会返回标准包络，可以取出可读文案
    const detail = await readErrorMessage(response);
    onEvent({ event: 'error', sessionId: sessionId ?? '', errorMessage: detail });
    return;
  }

  if (!response.body) {
    onEvent({ event: 'error', sessionId: sessionId ?? '', errorMessage: '当前环境不支持流式响应' });
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();

  const parser = createParser({
    onEvent: (sseEvent) => {
      // 后端只发 data 行（无 event: 字段）。v3 解析器对无 event: 的帧不填默认值
      // （e.event === undefined），只过滤显式使用了其它事件类型的帧。
      if (sseEvent.event !== undefined && sseEvent.event !== 'message') return;
      if (sseEvent.data === '') return;
      try {
        onEvent(JSON.parse(sseEvent.data) as ChatStreamEvent);
      } catch {
        // 单帧解析失败不应该中断整条流：跳过它，继续消费后面的帧
        console.warn('[stream] 丢弃无法解析的帧', sseEvent.data);
      }
    },
  });

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      parser.feed(decoder.decode(value, { stream: true }));
    }
    // 兼容最后一帧没有空行结尾的宽容语义（与手写版行为对齐）
    parser.feed('\n\n');
  } finally {
    reader.releaseLock();
  }
}

async function readErrorMessage(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { message?: string };
    if (typeof body.message === 'string' && body.message !== '') return body.message;
  } catch {
    /* 响应体不是 JSON，退回状态码文案 */
  }
  return `请求失败（HTTP ${response.status}）`;
}
